import React, { useEffect, useState } from "react";
import { CalendarDays, Landmark, Vote, ArrowDownLeft, ArrowUpRight, Lock } from "lucide-react";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { inr, stars, catLabel } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const statusCls = {
  voting: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
};

export default function MemberDetailModal({ memberId, open, onOpenChange }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && memberId) {
      setData(null);
      api.get(`/members/${memberId}/detail`).then((r) => setData(r.data));
    }
  }, [open, memberId]);

  const m = data?.member;
  const cat = m ? catLabel(m.category) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="member-detail-modal" className="max-w-sm rounded-2xl p-0 overflow-hidden max-h-[88vh] overflow-y-auto">
        {!data ? (
          <p className="text-center text-muted-foreground py-16">लोड हो रहा है…</p>
        ) : (
          <>
            {/* Profile header */}
            <div className="bg-primary text-white p-5">
              <div className="flex items-center gap-3">
                <Avatar name={m.name} size={56} />
                <div>
                  <DialogHeader>
                    <DialogTitle className="text-white font-display text-xl">{m.name} • {m.name_hi}</DialogTitle>
                  </DialogHeader>
                  <p className="text-white/70 text-xs font-mono">{m.member_code}</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cat.cls}`}>{cat.hi} / {cat.en}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-white/85">
                <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> जुड़े: {m.join_date}</span>
                <span>योगदान: {inr(m.contribution)}</span>
              </div>
            </div>

            <div className="p-5 space-y-4">
              {/* Stats (full view only) */}
              {data.full ? (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-blue-50 p-2"><p className="text-[10px] text-muted-foreground">Savings</p><p className="font-mono font-bold text-sm">{inr(m.savings)}</p></div>
                  <div className="rounded-xl bg-blue-50 p-2"><p className="text-[10px] text-muted-foreground">Loan बाकी</p><p className="font-mono font-bold text-sm">{inr(m.loan_remaining)}</p></div>
                  <div className="rounded-xl bg-blue-50 p-2"><p className="text-[10px] text-muted-foreground">Credit</p><p className="text-amber-500 text-sm">{stars(m.credit_score)}</p></div>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-xl bg-slate-50 p-3 text-xs text-muted-foreground">
                  <Lock className="h-4 w-4" /> प्राइवेसी: दूसरे सदस्य की बैलेंस जानकारी छुपी है। ऋण: {m.loan_remaining > 0 ? inr(m.loan_remaining) : "—"}
                </div>
              )}

              {/* Loan requests */}
              <div>
                <p className="font-display font-bold flex items-center gap-1.5 mb-2"><Landmark className="h-4 w-4 text-primary" /> ऋण इतिहास / Loan Requests</p>
                {data.loan_requests.length === 0 ? (
                  <p className="text-xs text-muted-foreground">कोई ऋण अनुरोध नहीं</p>
                ) : (
                  <div className="space-y-2">
                    {data.loan_requests.map((r) => (
                      <div key={r.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-2.5" data-testid={`detail-loan-${r.id}`}>
                        <div><p className="text-sm font-mono font-semibold">{inr(r.amount)}</p><p className="text-[11px] text-muted-foreground">{r.purpose}</p></div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusCls[r.status]}`}>{r.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Voting record */}
              <div>
                <p className="font-display font-bold flex items-center gap-1.5 mb-2"><Vote className="h-4 w-4 text-primary" /> मतदान रिकॉर्ड / Voting Record</p>
                {data.votes_cast.length === 0 ? (
                  <p className="text-xs text-muted-foreground">अभी तक कोई मतदान नहीं</p>
                ) : (
                  <div className="space-y-2">
                    {data.votes_cast.map((v) => (
                      <div key={v.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-2.5" data-testid={`detail-vote-${v.id}`}>
                        <div><p className="text-sm font-medium">{v.member_name}</p><p className="text-[11px] text-muted-foreground font-mono">{inr(v.amount)}</p></div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${v.vote === "approve" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{v.vote === "approve" ? "अनुमोदन" : "अस्वीकार"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transactions (full only) */}
              {data.full && data.transactions.length > 0 && (
                <div>
                  <p className="font-display font-bold mb-2">लेन-देन / Transactions</p>
                  <div className="space-y-2">
                    {data.transactions.slice(0, 8).map((t) => {
                      const j = t.type === "jama";
                      return (
                        <div key={t.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={`h-7 w-7 rounded-full flex items-center justify-center ${j ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>{j ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}</div>
                            <span className="text-xs">{t.note}</span>
                          </div>
                          <span className={`font-mono text-xs font-semibold ${j ? "text-emerald-600" : "text-red-600"}`}>{j ? "+" : "-"}{inr(t.amount)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
